import { useEffect } from 'react'
import { supabase } from '@/shared/utils/supabase'
import { AuthApi } from '@/shared/api/auth'
import { useAuthStore } from '@/stores/authStore'
import { resetTripFetchState } from '@/modules/trips/hooks/useTrips'

// Module-level guards so initialization runs exactly once regardless of
// React Strict Mode double-mounting or multiple AuthProvider renders.
let hasInitialized = false
let isInitializing = false

async function getStoredToken(): Promise<string | null> {
  return localStorage.getItem('access_token')
}

// Single shared AuthApi instance — not tied to any component lifecycle.
const authApi = new AuthApi(getStoredToken)

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const setUser = useAuthStore(state => state.setUser)
  const setLoading = useAuthStore(state => state.setLoading)
  const logout = useAuthStore(state => state.logout)

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {

      if (event === 'INITIAL_SESSION') {
        if (session && !hasInitialized && !isInitializing) {
          isInitializing = true
          setLoading(true)
          try {
            localStorage.setItem('access_token', session.access_token)
            localStorage.setItem('refresh_token', session.refresh_token)
            supabase.realtime.setAuth(session.access_token)
            const userProfile = await authApi.getProfile()
            setUser(userProfile)
          } catch {
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            await supabase.auth.signOut()
          } finally {
            hasInitialized = true
            isInitializing = false
            setLoading(false)
          }
        } else {
          hasInitialized = true
        }
      }

      // Supabase silently rotated the access token — keep our localStorage
      // keys in sync so every outgoing API request picks up the fresh token.
      if (event === 'TOKEN_REFRESHED' && session) {
        localStorage.setItem('access_token', session.access_token)
        localStorage.setItem('refresh_token', session.refresh_token)
        supabase.realtime.setAuth(session.access_token)
      }

      if (event === 'SIGNED_OUT') {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        supabase.realtime.setAuth(null)
        logout()
        resetTripFetchState()
      }
    })

    return () => subscription.unsubscribe()
  }, [setUser, setLoading, logout])

  return <>{children}</>
}
